import { AxiosResponse } from 'axios'
import { i18n } from 'i18next'
import _ from 'lodash'
import { all, call, getContext, put, select, takeEvery } from 'redux-saga/effects'
import { getType } from 'typesafe-actions'

import { apiCall } from '../../utils/api'
import { Dictionary } from '../../utils/types'
import { getCountryCode } from '../config'
import { withLoadingParametrized } from '../loading'

import { DomainsActions } from './actions'
import { getDomains, getStatesByCountryCode, getTownsByStateCode } from './selectors'
import { DomainData, DomainType, OTHER_OPTION, State, Town } from './types'
import { generateStatesLoadingCode, generateTownsLoadingCode } from './utils'

type DomainDataResponse = {
  code: string
  description?: string
  [key: string]: unknown
}

type DomainsResponse = {
  domains: Record<DomainType, DomainDataResponse[]>
}

type UmbracoDomainResponse = Dictionary<Dictionary<string>>

const mapDomainDescription = (items: DomainDataResponse[], translations: Dictionary<string>) =>
  _.map(items, x => ({
    ...x,
    description: translations[x.code] || x.description || ''
  }))

const sortDomainValues = (data: DomainData[]) => _.sortBy(data, 'description')

export function* loadDomains(action: ReturnType<typeof DomainsActions.loadDomains>) {
  const { domainTypes, language, adminxCall } = action.payload

  const domains: Dictionary<any> = yield select(getDomains, { domains: domainTypes, language })

  const notLoadedDomainTypes = _.keys(_.pickBy(domains, value => _.isEmpty(value))) as DomainType[]

  if (_.isEmpty(notLoadedDomainTypes)) return

  const countryCode: string = yield select(getCountryCode)

  const [{ data: domainData }, { data: umbracoData }]: [
    AxiosResponse<DomainsResponse>,
    AxiosResponse<UmbracoDomainResponse>
  ] = yield all([
    call(apiCall, {
      // ensure that relative path is used to allow SSR which accesses app from different URL than public
      url: '/api/Domain/Get',
      method: 'get',
      params: {
        countryCode,
        domains: notLoadedDomainTypes,
        isAdminxCall: adminxCall
      }
    }),
    call(apiCall, {
      // ensure that relative path is used to allow SSR which accesses app from different URL than public
      url: '/api/translation/domain',
      method: 'post',
      data: {
        countryCode,
        domainTypes: notLoadedDomainTypes
      },
      umbracoCall: true
    })
  ])

  for (let i = 0; i < notLoadedDomainTypes.length; i++) {
    const domain = notLoadedDomainTypes[i]
    if (domainData.domains[domain] && umbracoData[domain]) {
      yield put(
        DomainsActions.setDomainData({
          domainType: domain,
          data: sortDomainValues(mapDomainDescription(domainData.domains[domain], umbracoData[domain])),
          language: language
        })
      )
    }
  }
}

type StateResponse = {
  code: string
  countryCode: string
}

type StatesResponse = {
  states: StateResponse[]
}

type UmbracoStateResponse = Dictionary<string>

export function* loadStates(action: ReturnType<typeof DomainsActions.loadStates>) {
  const { countryCode, language } = action.payload

  const states: State[] | undefined = yield select(getStatesByCountryCode, { code: countryCode, language })

  if (!_.isEmpty(states)) return

  const [{ data: statesData }, { data: translations }]: [
    AxiosResponse<StatesResponse>,
    AxiosResponse<UmbracoStateResponse>
  ] = yield all([
    call(apiCall, {
      url: '/api/Domain/state',
      method: 'get',
      params: {
        country: countryCode
      }
    }),
    call(apiCall, {
      url: '/api/translation/domainState',
      method: 'get',
      params: {
        countryCode
      },
      umbracoCall: true
    })
  ])

  yield put(
    DomainsActions.setStates({
      data: sortDomainValues(mapDomainDescription(statesData.states, translations)) as State[],
      countryCode,
      language
    })
  )
}

type UmbracoTownsResponse = Dictionary<string>

type TownsResponse = {
  towns: {
    code: string
    stateCode: string
  }[]
}

export function* loadTowns(action: ReturnType<typeof DomainsActions.loadTowns>) {
  const { stateCode, language, countryCode } = action.payload

  const towns: Town[] | undefined = yield select(getTownsByStateCode, { code: stateCode, language })

  if (!_.isEmpty(towns)) return

  const i18n: i18n = yield getContext('i18n')

  const [{ data: townsResponse }, { data: translations }]: [
    AxiosResponse<TownsResponse>,
    AxiosResponse<UmbracoTownsResponse>
  ] = yield all([
    call(apiCall, {
      url: '/api/Domain/town',
      method: 'get',
      params: {
        state: stateCode
      }
    }),
    call(apiCall, {
      url: '/api/translation/domainTown',
      method: 'get',
      params: {
        countryCode,
        stateCode
      },
      umbracoCall: true
    })
  ])

  yield put(
    DomainsActions.setTowns({
      data: [
        ...sortDomainValues(mapDomainDescription(townsResponse.towns, translations)),
        {
          code: OTHER_OPTION,
          description: i18n.t('core:Other')
        }
      ] as Town[],
      stateCode,
      language
    })
  )
}

/* istanbul ignore next */
export function* domainSagas() {
  yield all([
    takeEvery(
      getType(DomainsActions.loadDomains),
      withLoadingParametrized('domains', loadDomains, x => x.payload.domainTypes)
    ),
    takeEvery(
      getType(DomainsActions.loadStates),
      withLoadingParametrized('domains', loadStates, x => generateStatesLoadingCode(x.payload.countryCode))
    ),
    takeEvery(
      getType(DomainsActions.loadTowns),
      withLoadingParametrized('domains', loadTowns, x => generateTownsLoadingCode(x.payload.stateCode))
    )
  ])
}
